import { useEffect, useRef, useState, ReactNode } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Select,
  Spinner,
  TableContainer,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Tag } from "../../models/Filter";
import { PieChart } from "@mui/x-charts";
import { getDashboard } from "../../services/processService";
import {
  DashboardViewModel,
  GraphViewModel,
  dashboardColors as colors,
  getChannelColor,
} from "../../models/Dashboard";
import { useMediaQuery } from "react-responsive";
import Filter from "../../components/Filter";
import DataList from "./components/DataList";
import SupliersTable from "./components/SuppliersTable";
import DatePickerField from "../../components/DatePickerField";
import {
  FaArrowDown,
  FaArrowUp,
  FaBoxOpen,
  FaDollarSign,
  FaShip,
  FaClipboardCheck,
  FaClock,
  FaWarehouse,
} from "react-icons/fa";
import generatePDF, { Resolution, Margin } from "react-to-pdf";
import HomeHeader from "../../components/Header/HomeHeader";
import FilterModal from "../../components/FilterModal";
import { useCompany } from "../../contexts/CompanyContext";
import Layout from "../../components/Layout";
import { crm } from "../../theme";

// KPI card — soft-brand icon circle + big value + colored trend pill.
const Kpi = ({
  title,
  value,
  before,
  icon,
  w,
}: {
  title: string;
  value: ReactNode;
  before?: number;
  icon: ReactNode;
  w?: any;
}) => {
  const up = (before ?? 0) > 0;
  return (
    <Flex className="dashboard-card" width={w} gap="10px">
      <Flex
        align="center"
        justify="center"
        boxSize="40px"
        borderRadius="full"
        bg={crm.brandSoft}
        color={crm.brand}
        mb="4px"
      >
        {icon}
      </Flex>
      <Text fontSize="sm" color={crm.textMuted} fontWeight="600">
        {title}
      </Text>
      <Text fontSize="32px" fontWeight="700" color={crm.text} lineHeight="1.1">
        {value}
      </Text>
      {before ? (
        <Flex
          align="center"
          gap="4px"
          px="8px"
          py="2px"
          borderRadius="full"
          w="fit-content"
          bg={up ? "#dcfce7" : "#fee2e2"}
          color={up ? crm.success : crm.danger}
          fontSize="11px"
          fontWeight="600"
        >
          {up ? <FaArrowUp /> : <FaArrowDown />}
          {Math.abs(before)}% P.A.
        </Flex>
      ) : null}
    </Flex>
  );
};

const Dashboard = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [dashboardData, setDashboardData] = useState<DashboardViewModel>();
  const [graphs, setGraphs] = useState<GraphViewModel[]>([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [graphType, setGraphType] = useState<string>("1");
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState<Tag[]>([]);
  const [initialDate, setInitialDate] = useState<Date>();
  const [initialDateValue, setInitialDateValue] = useState<string>();
  const [finalDate, setFinalDate] = useState<Date>();
  const [importMode, setImportMode] = useState<string>();
  const [finalDateValue, setFinalDateValue] = useState<string>();
  const formatter = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "USD",
  });

  const { currentCompany, currentEmulation } = useCompany();

  const { isOpen, onOpen, onClose } = useDisclosure();
  useEffect(() => {
    get();
  }, [tags, graphType, currentEmulation, currentCompany, importMode]);

  useEffect(() => {
    if (initialDate && finalDate) get();
  }, [initialDate, finalDate]);

  const get = async () => {
    setLoading(true);
    const response: DashboardViewModel = await getDashboard({
      graphType,
      initialDate: initialDateValue,
      finalDate: finalDateValue,
      type: currentCompany,
      importMode,
      companyId: currentEmulation,
      ncm: tags
        .filter((t) => t.type === "ncm")
        ?.map((t) => t.value)
        .join("|"),
      cvfRef: tags
        .filter((t) => t.type === "cvfRef")
        ?.map((t) => t.value)
        .join("|"),
      countryOfOrigin: tags
        .filter((t) => t.type === "origin")
        ?.map((t) => t.value)
        .join("|"),
      exporter: tags
        .filter((t) => t.type === "supplier")
        ?.map((t) => t.value)
        .join("|"),
      product: tags
        .filter((t) => t.type === "product")
        ?.map((t) => t.value)
        .join("|"),
    });

    if (response) {
      setDashboardData(response);
      setGraphs((a) => {
        let newGraphs = [...response.graphs];
        // newGraphs = newGraphs.filter((g) =>
        //   selectedGraphs.some((sg) => g.title === sg)
        // );
        return newGraphs;
      });
    }
    setLoading(false);
  };
  const targetRef = useRef(null);
  const options = {
    resolution: Resolution.LOW,
    page: {
      // margin is in MM, default is Margin.NONE = 0
      margin: Margin.SMALL,
    },
    overrides: {
      // see https://artskydj.github.io/jsPDF/docs/jsPDF.html for more options
      pdf: {
        compress: false,
      },
    },
  };

  const handleGeneratePDF = () => {
    setIsPrinting(true);
    setTimeout(() => {
      setIsPrinting(false);
    }, 600);
  };

  useEffect(() => {
    if (isPrinting)
      setTimeout(() => {
        generatePDF(targetRef, options);
      }, 100);
  }, [isPrinting]);

  const extraFilters = [
    <Box>
      <Heading size="sm" mb="8px">
        Valores dos gráficos
      </Heading>
      <Select
        w={{ base: undefined, md: "16vw" }}
        backgroundColor="white"
        value={graphType}
        onChange={(e) => setGraphType(e.target.value)}
      >
        <option value="1">Por invoice</option>
        <option value="2">Por nº de processo</option>
      </Select>
    </Box>,
    <Box>
      <Heading size="sm" mb="8px">
        Início do período
      </Heading>
      <DatePickerField
        width={isMobile ? "80vw" : "16vw"}
        date={initialDate}
        setDate={setInitialDate}
        dateValue={initialDateValue}
        setDateValue={setInitialDateValue}
      />
    </Box>,
    <Box>
      <Heading size="sm" mb="8px">
        Fim do período
      </Heading>
      <DatePickerField
        width={isMobile ? "80vw" : "16vw"}
        date={finalDate}
        setDate={setFinalDate}
        dateValue={finalDateValue}
        setDateValue={setFinalDateValue}
      />
    </Box>,
    <Box>
      <Heading size="sm" mb="8px">
        Modo de importação
      </Heading>
      <Select
        onChange={(e) =>
          !e.target.value
            ? setImportMode(undefined)
            : setImportMode(e.target.value)
        }
        backgroundColor="white"
        width={isMobile ? "80vw" : "16vw"}
      >
        <option value={undefined}></option>
        <option>Encomenda</option>
        <option>Distribuição</option>
        <option>Conta e Ordem</option>
      </Select>
    </Box>,
    <Button
      pos="relative"
      top="24px"
      backgroundColor={crm.brand}
      _hover={{ backgroundColor: crm.brandDark }}
      borderRadius="10px"
      color="white"
      onClick={handleGeneratePDF}
    >
      Gerar pdf
    </Button>,
  ];
  return (
    <Box height="100%">
      <Layout>
        <Flex
          backgroundColor="var(--main-bg-color)"
          alignItems="center"
          flexDirection="column"
        >
          <Flex width="90%" mt="48px" mb="24px" align="center" gap="12px">
            <Box w="4px" h="34px" bg={crm.brand} borderRadius="full" />
            <Box>
              <Text fontSize="28px" fontWeight="700" color={crm.text} lineHeight="1.1">
                Dashboard
              </Text>
              <Text color={crm.textMuted} fontSize="sm" mt="2px">
                Visão geral de processos, valores e indicadores.
              </Text>
            </Box>
          </Flex>
          <Flex
            backgroundColor={crm.surface}
            border={`1px solid ${crm.border}`}
            boxShadow={crm.shadowSm}
            borderRadius="12px"
            width="90%"
            p="32px"
            mb="48px"
          >
            <FilterModal
              extraFilters={extraFilters}
              {...{
                isOpen,
                onClose,
                tags,
                setTags,
              }}
            />
            {isMobile ? (
              <Flex w="100%" alignItems="center" flexDir="column">
                <Button
                  w="80%"
                  backgroundColor="var(--icon-color)"
                  color="white"
                  onClick={onOpen}
                >
                  Filtrar documentos
                </Button>
              </Flex>
            ) : (
              <Filter
                {...{
                  extraFilters,
                  tags,
                  setTags,
                }}
              />
            )}
          </Flex>
          <Box ref={targetRef} w={{ base: "95%", md: "90%" }}>
            {isPrinting && (
              <Flex w="100%" justifyContent="flex-end">
                <Image
                  src={
                    process.env.REACT_APP_LOGO === "syntra"
                      ? "syntraLogo.png"
                      : "grupoCvfLogo.png"
                  }
                  height="80px"
                  marginLeft="12px"
                />
              </Flex>
            )}
            {initialDate && finalDate && (
              <Heading mb="24px" fontWeight="normal" size="md">
                Período: {initialDateValue} - {finalDateValue}
              </Heading>
            )}
            <Flex flexDirection={isMobile ? "column" : "row"} gap="12px">
              <Flex
                p="24px"
                backgroundColor="white"
                width={{ base: "100%", md: "30%" }}
                minHeight="500px"
                flexDir="column"
                borderRadius="12px"
                border={`1px solid ${crm.border}`}
                boxShadow={crm.shadowSm}
              >
                <Heading size="md" mb="48px" fontWeight="semibold" color={crm.text}>
                  Canais
                </Heading>
                <Flex
                  justifyContent="center"
                  h="300px"
                  w={{
                    base: "100%",
                    md: graphs[0]?.values.length === 0 ? "100%" : "125%",
                  }}
                >
                  {loading ? (
                    <Spinner size="xl" />
                  ) : graphs[0]?.values.length === 0 ? (
                    <Flex justifyContent="center" alignItems="center">
                      Ainda não há dados de canais.
                    </Flex>
                  ) : (
                    <PieChart
                      slotProps={{ legend: { hidden: true } }}
                      colors={colors} // Use palette
                      series={[
                        {
                          data:
                            graphs[0]?.values.map((v, i) => ({
                              label: v.label,
                              value:
                                graphType === "1" ? v.usd : v.numberOfProcesses,
                              color: getChannelColor(v.label, i),
                            })) ?? [],
                          innerRadius: isMobile ? 90 : 100,
                          outerRadius: isMobile ? 130 : 150,
                          cx: isMobile ? 145 : undefined,
                        },
                      ]}
                    />
                  )}
                </Flex>
                {graphs[0]?.before ? (
                  <Flex w="100%" fontWeight="normal" justifyContent="center">
                    <Text
                      fontWeight="semibold"
                      size="sm"
                      color={(graphs[0]?.before ?? 0) > 0 ? "green" : "red"}
                    >
                      <Flex gap="4px" mr="4px" alignItems="center">
                        {(graphs[0]?.before ?? 0) > 0 ? (
                          <FaArrowUp />
                        ) : (
                          <FaArrowDown />
                        )}
                        {Math.abs(graphs[0]?.before)}%
                      </Flex>
                    </Text>
                    <Text size="sm">
                      {(graphs[0]?.before ?? 0) > 0 ? "maior" : "menor"} que o
                      período anterior
                    </Text>
                  </Flex>
                ) : (
                  <Text></Text>
                )}
              </Flex>
              <Flex
                backgroundColor="white"
                width={{ base: "100%", md: "70%" }}
                minHeight="500px"
                flexDir="column"
                borderRadius="12px"
                border={`1px solid ${crm.border}`}
                boxShadow={crm.shadowSm}
              >
                <Heading p="27px" size="md" fontWeight="semibold" color={crm.text}>
                  Fornecedores
                </Heading>
                {loading ? (
                  <Flex
                    justifyContent="center"
                    alignItems="center"
                    w="100%"
                    h="100%"
                  >
                    <Spinner size="xl" />
                  </Flex>
                ) : (
                  <TableContainer>
                    <SupliersTable
                      graphType={graphType}
                      formatter={formatter}
                      page={
                        graphs
                          .find((g) => g.title === "Fornecedor")
                          ?.values.slice(0, 5) ?? []
                      }
                    />
                  </TableContainer>
                )}
              </Flex>
            </Flex>
            <Flex
              flexDirection={isMobile ? "column" : "row"}
              gap="12px"
              mb="24px"
              mt="24px"
            >
              <Kpi
                w={{ base: "100%", md: "30%" }}
                icon={<FaBoxOpen />}
                title="Processos"
                value={dashboardData?.count}
                before={dashboardData?.countBefore}
              />

              <Kpi
                w={{ base: "100%", md: "30%" }}
                icon={<FaWarehouse />}
                title="Containers em produção"
                value={dashboardData?.containersInProduction}
              />

              <Kpi
                w={{ base: "100%", md: undefined }}
                icon={<FaDollarSign />}
                title="Total invoice"
                value={`U$ ${dashboardData?.totalInvoice ?? ""}`}
                before={dashboardData?.totalInvoiceBefore}
              />
              {dashboardData?.transitTime && (
                <Kpi
                  w={{ base: "100%", md: "50%" }}
                  icon={<FaShip />}
                  title="Transit time"
                  value={`${dashboardData?.transitTime} dias`}
                  before={dashboardData?.transitTimeBefore}
                />
              )}
              {dashboardData?.nacionalization && (
                <Kpi
                  w={{ base: "100%", md: "50%" }}
                  icon={<FaClipboardCheck />}
                  title="Nacionalização"
                  value={`${dashboardData.nacionalization} dias`}
                  before={dashboardData?.nacionalizationBefore}
                />
              )}
              {dashboardData?.leadTime && (
                <Kpi
                  w={{ base: "100%", md: "50%" }}
                  icon={<FaClock />}
                  title="Lead time"
                  value={`${dashboardData?.leadTime} dias`}
                  before={dashboardData?.leadTimeBefore}
                />
              )}
            </Flex>
            <Flex
              className="dashboard-card"
              borderRadius="8px"
              mb="24px"
              width={{ base: "100%", md: undefined }}
            >
              <HomeHeader />
            </Flex>
            <Flex
              flexDirection={isMobile ? "column" : "row"}
              gap="12px"
              mb="48px"
            >
              <DataList
                isPrinting={isPrinting}
                loading={loading}
                title="Países de origem"
                button="TODAS AS ORIGENS"
                graph={graphs.find((g) => g.title === "Origem")}
                {...{ formatter, graphType }}
              />
              <DataList
                isPrinting={isPrinting}
                loading={loading}
                title="Fornecedores"
                button="TODOS OS FORNECEDORES"
                graph={graphs.find((g) => g.title === "Fornecedor")}
                {...{ formatter, graphType }}
              />
              <DataList
                isPrinting={isPrinting}
                loading={loading}
                title="Agentes de carga"
                button="TODOS OS ARMADORES"
                graph={graphs.find((g) => g.title === "Armador")}
                {...{ formatter, graphType }}
              />
            </Flex>
          </Box>
        </Flex>
      </Layout>
    </Box>
  );
};
export default Dashboard;
